import { GenderEnum } from 'types/generated/client';
import BirthdayCake from 'svg/BirthdayCake';
import GenderAll from 'svg/GenderAll';
import GenderFemale from 'svg/GenderFemale';
import GenderMale from 'svg/GenderMale';
import type { PlayerProfileView } from 'screens/PlayerProfile/types';
import { formatBirthday } from 'screens/PlayerProfile/utils';
import ProfileSection from '../ProfileSection';

interface Props {
  profile: PlayerProfileView;
  isViewerProfile: boolean;
  onEdit?: () => void;
}

/** Only the genders with a designed glyph get an icon row; the rest render as text. */
const GENDER_ICONS: Partial<Record<GenderEnum, (props: { className?: string }) => JSX.Element>> = {
  [GenderEnum.Male]: GenderMale,
  [GenderEnum.Female]: GenderFemale,
  [GenderEnum.Nonbinary]: GenderAll,
};

const GENDER_LABELS: Partial<Record<GenderEnum, string>> = {
  [GenderEnum.Male]: 'Male',
  [GenderEnum.Female]: 'Female',
  [GenderEnum.Nonbinary]: 'Nonbinary',
};

export default function ProfileAboutMe({ profile, isViewerProfile, onEdit }: Props) {
  const { gender } = profile;
  const GenderIcon = gender ? GENDER_ICONS[gender] : undefined;
  const genderLabel = gender ? GENDER_LABELS[gender] : undefined;
  const birthdayLabel = formatBirthday(profile.birthday);
  const isEmpty = !profile.aboutMe && !genderLabel && !birthdayLabel;

  return (
    <ProfileSection
      title="About Me"
      isViewerProfile={isViewerProfile}
      onEdit={onEdit}
      isEmpty={isEmpty}
      emptyLabel="Help players get to know you"
    >
      <div className="flex flex-col gap-ds-md md:gap-ds-sm">
        {(genderLabel || birthdayLabel) && (
          <div className="flex flex-col gap-ds-sm md:gap-ds-xs">
            {genderLabel && (
              <p className="typography-product-element-label md:typography-product-body flex items-center gap-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {GenderIcon && <GenderIcon className="size-6 shrink-0" />}
                <span>{genderLabel}</span>
              </p>
            )}
            {birthdayLabel && (
              <p className="typography-product-element-label md:typography-product-body flex items-center gap-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                <BirthdayCake className="size-6 shrink-0 text-brand-fire-600" />
                <span>{birthdayLabel}</span>
              </p>
            )}
          </div>
        )}

        {profile.aboutMe && (
          <p className="typography-product-body whitespace-pre-line text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {profile.aboutMe}
          </p>
        )}
      </div>
    </ProfileSection>
  );
}
